import React from 'react';
import { ComponentSkinProps } from '../../../../types/component-skin';
import { AssetHistoryLogicReturn, formatDate, formatAmount, isP2PEnabled } from '../AssetHistoryLogic';
import { QRCodeSVG } from 'qrcode.react';
import clsx from 'clsx';
import '../AssetHistory.css';

// Icon components
const PointIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="8" x2="12" y2="16"></line>
    <line x1="8" y1="12" x2="16" y2="12"></line>
  </svg>
);

const ArrowDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);

const ExchangeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <path d="M8 12h8M12 16l4-4-4-4"></path>
  </svg>
);

const AssetIcon: React.FC<{iconUrl?: string, assetName?: string}> = ({ iconUrl, assetName = '' }) => {
  return (
    <div
      className="asset-icon-dollar"
      style={{
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        border: '1px solid #ddd',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8f9fa',
        marginRight: '8px',
        color: '#555',
        fontSize: '14px',
        fontWeight: 'bold'
      }}
    >
      <PointIcon />
    </div>
  );
};

/**
 * AssetHistory 컴포넌트의 기본 스킨
 */
const BasicAssetHistorySkin: React.FC<AssetHistoryLogicReturn> = ({
    // Props
    title,
    emptyText,
    style,
    className,
    isPreview,
    
    // Redux state
    assetTransactionsState,
    balances,
    totalBalance,
    isUserLoggedIn,
    userInfo,
    
    // Local state
    assetData,
    totalSum,
    totalGotMileage,
    totalCount,
    apiError,
    isLoading,
    assetTypes,
    selectedAssetType,
    assetTypeBalances,
    
    // Modal states
    withdrawState,
    exchangeState,
    depositState,
    p2pState,
    
    // Preview states
    previewSelectedAssetType,
    previewShowWithdrawForm,
    previewShowExchangeModal,
    
    // Functions
    handleAssetTypeChange,
    toggleWithdrawForm,
    openDepositModal,
    closeDepositModal,
    copyWalletAddress,
    closeExchangeModal,
    openExchangeModal,
    handleExchangeAmountChange,
    handleExchangeSubmit,
    handleWithdrawAmountChange,
    handleWithdrawSubmit,
    openP2PModal,
    closeP2PModal,
    handleP2PSubmit,
    setWithdrawState,
    setExchangeState,
    setP2PState,
    
    // Utils
    t
}) => {

  // Preview mode data
  const previewAssetData = [
    {
      id: 1,
      amount: 1000,
      description: '주문 완료 - ORDER#12345',
      createdAt: new Date().toISOString(),
      transactionType: 'order-normal',
      assetType: { id: 1, name: '포인트' },
      companyAssetType: { id: 1, displayName: '위드쿠키 포인트' }
    },
    {
      id: 2,
      amount: -500,
      description: '상품 구매',
      createdAt: new Date(Date.now() - 86400000).toISOString(),
      transactionType: 'order-normal',
      assetType: { id: 1, name: '포인트' },
      companyAssetType: { id: 1, displayName: '위드쿠키 포인트' }
    }
  ];

  const displayData = isPreview ? previewAssetData : assetData;

  // Error or login required state
  if (!isUserLoggedIn && !isPreview) {
    return (
      <div className={clsx(className, 'asset-history-login-required')} style={style}>
        <div className="login-message">
          <h3>{t('로그인이 필요한 서비스입니다.')}</h3>
          <p>{t('자산 내역을 확인하려면 로그인해 주세요.')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={clsx(className, 'asset-history-wrapper')} style={style}>
      {/* Header */}
      <div className="asset-history-header">
        <h2 className="asset-history-title">{title}</h2>
        
        {/* Asset Type Filter */}
        {assetTypes.length > 0 && (
          <div className="asset-type-filter">
            <select 
              value={selectedAssetType} 
              onChange={handleAssetTypeChange}
              className="asset-type-select"
            >
              <option value="">{t('전체')}</option>
              {assetTypes.map((type: any) => (
                <option key={type.id} value={type.id}>
                  {type.name || type.displayName}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Balance Section */}
      {balances.length > 0 && (
        <div className="asset-balance-section">
          <div className="balance-cards">
            {balances.map((balance: any) => (
              <div key={balance.assetTypeId} className="balance-card">
                <div className="balance-header">
                  <AssetIcon iconUrl={balance.iconUrl} assetName={balance.assetName} />
                  <span className="asset-name">{balance.displayName || balance.assetName}</span>
                </div>
                <div className="balance-amount">
                  {formatAmount(Number(balance.amount))}
                </div>
                <div className="balance-actions">
                  <button 
                    className="btn-small btn-deposit"
                    onClick={() => openDepositModal(balance)}
                  >
                    {t('입금')}
                  </button>
                  {balance.withdrawal?.enabled && (
                    <button 
                      className="btn-small btn-withdraw"
                      onClick={toggleWithdrawForm}
                    >
                      {t('출금')}
                    </button>
                  )}
                  {balance.exchange?.enabled && (
                    <button 
                      className="btn-small btn-exchange"
                      onClick={() => openExchangeModal(balance)}
                    >
                      {t('환전')}
                    </button>
                  )}
                  {isP2PEnabled(balance) && (
                    <button 
                      className="btn-small btn-p2p"
                      onClick={() => openP2PModal(balance)}
                    >
                      {t('P2P')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Transaction History */}
      <div className="asset-history-content">
        {isLoading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>{t('로딩 중...')}</p>
          </div>
        ) : displayData.length > 0 ? (
          <div className="transaction-list">
            {displayData.map((item: any) => (
              <div key={item.id} className="transaction-item">
                <div className="transaction-info">
                  <div className="transaction-header">
                    <span className="transaction-asset">
                      {item.companyAssetType?.displayName || item.assetType?.name || t('자산')}
                    </span>
                    <span className="transaction-date">
                      {formatDate(item.createdAt)}
                    </span>
                  </div>
                  <div className="transaction-description">
                    {item.description}
                  </div>
                </div>
                <div className={`transaction-amount ${item.amount > 0 ? 'positive' : 'negative'}`}>
                  {item.amount > 0 ? '+' : ''}{formatAmount(item.amount)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <p>{emptyText}</p>
          </div>
        )}
      </div>

      {/* Withdrawal Form Modal */}
      {withdrawState.showWithdrawForm && (
        <div className="modal-overlay" onClick={toggleWithdrawForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{t('출금 요청')}</h3>
            <form onSubmit={handleWithdrawSubmit}>
              <div className="form-group">
                <label>{t('출금 금액')}</label>
                <input
                  type="number"
                  value={withdrawState.withdrawAmount}
                  onChange={handleWithdrawAmountChange}
                  placeholder={t('금액을 입력하세요')}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('출금 주소')}</label>
                <input
                  type="text"
                  value={withdrawState.withdrawToAddress}
                  onChange={(e) => setWithdrawState((prev: any) => ({ ...prev, withdrawToAddress: e.target.value }))}
                  placeholder={t('출금 주소를 입력하세요')}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('메모 (선택)')}</label>
                <input
                  type="text"
                  value={withdrawState.withdrawMemo}
                  onChange={(e) => setWithdrawState((prev: any) => ({ ...prev, withdrawMemo: e.target.value }))}
                  placeholder={t('메모를 입력하세요')}
                />
              </div>
              {withdrawState.feeAmount > 0 && (
                <div className="fee-info">
                  <p>{t('수수료')}: {formatAmount(withdrawState.feeAmount)}</p>
                  <p>{t('실제 출금액')}: {formatAmount(withdrawState.receiveAmount)}</p>
                </div>
              )}
              <div className="modal-actions">
                <button type="button" onClick={toggleWithdrawForm} className="btn-cancel">
                  {t('취소')}
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={withdrawState.withdrawProcessing}
                >
                  {withdrawState.withdrawProcessing ? t('처리 중...') : t('출금 요청')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Exchange Modal */}
      {exchangeState.showExchangeModal && exchangeState.exchangeFromAsset && (
        <div className="modal-overlay" onClick={closeExchangeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{t('환전')}</h3>
            <div className="exchange-info">
              <p>{t('From')}: {exchangeState.exchangeFromAsset.displayName || exchangeState.exchangeFromAsset.assetName}</p>
              <p>{t('To')}: {exchangeState.exchangeToAsset?.displayName || exchangeState.exchangeToAsset?.assetName || t('선택하세요')}</p>
            </div>
            <form onSubmit={handleExchangeSubmit}>
              <div className="form-group">
                <label>{t('환전 금액')}</label>
                <input
                  type="number"
                  value={exchangeState.exchangeAmount}
                  onChange={(e) => handleExchangeAmountChange(e.target.value)}
                  placeholder={t('금액을 입력하세요')}
                  required
                />
              </div>
              {exchangeState.exchangeFeeAmount > 0 && (
                <div className="fee-info">
                  <p>{t('수수료')}: {formatAmount(exchangeState.exchangeFeeAmount)}</p>
                  <p>{t('환전 후 금액')}: {formatAmount(exchangeState.exchangeReceiveAmount)}</p>
                </div>
              )}
              <div className="modal-actions">
                <button type="button" onClick={closeExchangeModal} className="btn-cancel">
                  {t('취소')}
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={exchangeState.exchangeProcessing}
                >
                  {exchangeState.exchangeProcessing ? t('처리 중...') : t('환전하기')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Deposit Modal */}
      {depositState.showDepositModal && depositState.depositAsset && (
        <div className="modal-overlay" onClick={closeDepositModal}>
          <div className="modal-content deposit-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('입금')}</h3>
            <div className="deposit-info">
              <p className="asset-name">
                {depositState.depositAsset.displayName || depositState.depositAsset.assetName}
              </p>
              {depositState.depositAsset.wallet?.address && (
                <div className="wallet-info">
                  <div className="qr-code">
                    <QRCodeSVG value={depositState.depositAsset.wallet.address} size={200} />
                  </div>
                  <div className="address-section">
                    <label>{t('입금 주소')}</label>
                    <div className="address-input">
                      <input 
                        type="text" 
                        value={depositState.depositAsset.wallet.address} 
                        readOnly 
                      />
                      <button 
                        type="button"
                        onClick={() => depositState.depositAsset?.wallet?.address && copyWalletAddress(depositState.depositAsset.wallet.address)}
                        className="btn-copy"
                      >
                        {t('복사')}
                      </button>
                    </div>
                  </div>
                  {depositState.depositAsset.wallet.memo && (
                    <div className="memo-section">
                      <label>{t('메모')}</label>
                      <input 
                        type="text" 
                        value={depositState.depositAsset.wallet.memo} 
                        readOnly 
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button type="button" onClick={closeDepositModal} className="btn-primary">
                {t('닫기')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* P2P Modal */}
      {p2pState.showP2PModal && p2pState.p2pTargetAsset && (
        <div className="modal-overlay" onClick={closeP2PModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{t('P2P 전송')}</h3>
            <form onSubmit={handleP2PSubmit}>
              <div className="form-group">
                <label>{t('받는 사람 ID')}</label>
                <input
                  type="text"
                  value={p2pState.p2pToUserId}
                  onChange={(e) => setP2PState((prev: any) => ({ ...prev, p2pToUserId: e.target.value }))}
                  placeholder={t('유저 ID를 입력하세요')}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('전송 금액')}</label>
                <input
                  type="number"
                  value={p2pState.p2pAmount}
                  onChange={(e) => setP2PState((prev: any) => ({ ...prev, p2pAmount: e.target.value }))}
                  placeholder={t('금액을 입력하세요')}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('메시지 (선택)')}</label>
                <input
                  type="text"
                  value={p2pState.p2pReason}
                  onChange={(e) => setP2PState((prev: any) => ({ ...prev, p2pReason: e.target.value }))}
                  placeholder={t('메시지를 입력하세요')}
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={closeP2PModal} className="btn-cancel">
                  {t('취소')}
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={p2pState.p2pProcessing}
                >
                  {p2pState.p2pProcessing ? t('처리 중...') : t('전송하기')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BasicAssetHistorySkin;